/**
 * ACH form calls for the secure link flow (/link/ach): vendor-service's @SecureLinkEndpoint endpoints, authorized by the
 * secure link session (Authorization: Bearer). Never sends a loan id or token: the server takes the loan and borrower
 * from the session.
 *
 * The bank step uses the same method names as PrivatePlaidService, so AchFormBaseStore and the ACH screens work unchanged.
 */
const BASE = '/api/v1/vendor/secure-link/ach';

export type SecureLinkRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

const digits = (value: string) => (value ?? '').replace(/\D/g, '');

/** What the page shows (vendor-service AchLinkDataResponse): this borrower only, no bank details. */
export interface AchLinkData {
  loanId: number;
  borrowerFirstName?: string;
  borrowerNames?: string[];
  lenderName?: string;
  propertyAddresses?: string[];
  verificationStep?: string;
  withdrawalDay?: number;
  /** The Plaid feature is on for this loan (global flag and loan flag), whatever kind of link this is. */
  plaidEnabled?: boolean;
  /** What the link offers: MANUAL = typed bank details only, even where Plaid is on. Missing = a link made before modes. */
  entryMode?: 'PLAID' | 'MANUAL';
  /** The manual bank form is offered (fallback). */
  manualEntryEnabled?: boolean;
}

/** The bank step through Plaid: what Plaid Link returned plus the typed fields. Bank numbers never go through here. */
export interface PlaidConnectValues {
  publicToken: string;
  accountId: string;
  institutionId?: string;
  withdrawalDay: number;
}

/** What vendor-service linked: no numbers, only bank, type and last 4 digits. */
export interface PlaidConnectResult {
  bankName: string;
  accountType: string;
  accountMask: string;
}

/** data.reason of a refused Plaid step (vendor-service AchPlaidConnectException). */
export const PLAID_REASON = {
  MANUAL_ENTRY_REQUIRED: 'MANUAL_ENTRY_REQUIRED',
  RECONNECT_REQUIRED: 'RECONNECT_REQUIRED',
  PLAID_UNAVAILABLE: 'PLAID_UNAVAILABLE',
  PLAID_DISABLED: 'PLAID_DISABLED',
} as const;

/** The bank information form's values (BankInformationFormStore). */
export interface BankInformationValues {
  bankName: string;
  accountName: string;
  accountType: string;
  accountNumber: string;
  routingNumber: string;
  withdrawalDay: number;
}

export class SecureLinkAchService {
  constructor(private readonly request: SecureLinkRequest) {}

  /** Shaped like an axios response ({ data }), which is what AchFormBaseStore.fetchACHData reads. */
  getACHData() {
    return this.request<AchLinkData>(`${BASE}/data`).then(data => ({ data }));
  }

  /** The Bank of America check now runs server-side as part of saving the bank information. */
  sentBofaValidation(..._ignored: unknown[]) {
    return Promise.resolve();
  }

  saveBankInformation(values: BankInformationValues) {
    return this.request(`${BASE}/bank-information`, {
      method: 'POST',
      body: JSON.stringify({
        bankName: values.bankName,
        accountName: values.accountName,
        accountType: values.accountType,
        accountNumber: digits(values.accountNumber),
        routingNumber: digits(values.routingNumber),
        withdrawalDay: values.withdrawalDay,
      }),
    });
  }

  /** A Plaid Link token for this link's borrower (only when plaidEnabled). */
  getPlaidLinkToken(): Promise<string> {
    return this.request<{ linkToken: string }>(`${BASE}/plaid/link-token`, {
      method: 'POST',
      body: JSON.stringify({}),
    }).then(data => data.linkToken);
  }

  /** Links the account picked in Plaid Link; vendor-service reads the numbers from Plaid itself. */
  connectPlaidAccount(values: PlaidConnectValues): Promise<PlaidConnectResult> {
    return this.request<PlaidConnectResult>(`${BASE}/plaid/bank-account`, {
      method: 'POST',
      body: JSON.stringify(values),
    });
  }

  generateAchDocument(data: { signature: string; userAgent: string }) {
    return this.request(`${BASE}/sign`, {
      method: 'POST',
      body: JSON.stringify({ signature: data.signature, userAgent: data.userAgent }),
    });
  }
}
